"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeaderStrip from "./components/HeaderStrip";
import HeroSection from "./components/HeroSection";
import StatsSection from "./components/StatsSection";
import CommonProblems from "./components/CommonProblems";
import WhyFail from "./components/WhyFail";
import ProcessSection from "./components/ProcessSection";
import ScopeSection from "./components/ScopeSection";
import NeighborhoodCoverage from "./components/NeighborhoodCoverage";
import FaqSection from "@/components/feature/FaqSection";
import ServiceAreasGrid from "@/components/feature/ServiceAreasGrid";
import RelatedServicesBlockService from "@/components/feature/RelatedServicesBlockService";
import BottomCta from "./components/BottomCta";
import LeadFormSection from "@/components/feature/LeadFormSection";
import AboutServiceSection from "@/components/feature/AboutServiceSection";
import { injectMetaTags } from "@/lib/seoHelpers";

export default function SlidingDoorRepairPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const aboutImageSrc = currentLang === "es"
    ? "https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/bd2499d8-debf-4747-a18e-6d4b3ae9f63e_Sliding-Door-Repair-Miami-2.png?v=a0c53dd0158734361bfa1e21308c8560"
    : "https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/bd2499d8-debf-4747-a18e-6d4b3ae9f63e_Sliding-Door-Repair-Miami-2.png?v=a0c53dd0158734361bfa1e21308c8560";

  const slidingDoorFaqs = [
    {
      question: t("sliding.faq1Q") as string,
      answer: t("sliding.faq1A") as string,
    },
    {
      question: t("sliding.faq2Q") as string,
      answer: t("sliding.faq2A") as string,
    },
    {
      question: t("sliding.faq3Q") as string,
      answer: t("sliding.faq3A") as string,
    },
  ];

  useEffect(() => {
    document.title = t("sliding.seoTitle") as string;
  }, [currentLang, t]);

  useEffect(() => {
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", t("sliding.seoDesc") as string);
  }, [currentLang, t]);

  useEffect(() => {
    const cleanupSeo = injectMetaTags({
      title: t("sliding.seoTitle") as string,
      description: t("sliding.seoDesc") as string,
      url: "https://impact-window-repair-miami.com/sliding-door-repair/",
      image: "https://impact-window-repair-miami.com/og/og-sliding-door.jpg",
      imageAlt: t("sliding.ogImageAlt") as string,
      breadcrumbs: [
        { name: t("nav.home") as string, item: "https://impact-window-repair-miami.com/" },
        { name: t("sliding.breadcrumbName") as string, item: "https://impact-window-repair-miami.com/sliding-door-repair/" }
      ],
      type: "website",
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: t("sliding.schemaName") as string,
      provider: {
        "@type": "LocalBusiness",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        email: "contact@impact-window-repair-miami.com",
        url: "https://impact-window-repair-miami.com",
        inLanguage: currentLang === "es" ? "es-US" : "en-US",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
        geo: {
          "@type": "GeoCoordinates",
          "latitude": 25.7617,
          "longitude": -80.1918
        },
        openingHoursSpecification: [{
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "07:00",
          "closes": "21:00"
        }],
        priceRange: "$$",
        paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
        currenciesAccepted: "USD",
        areaServed: [
          {"@type": "City", "name": "Miami"},
          {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
          "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
        ],
        knowsLanguage: ["en", "es"],
        knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
        slogan: t("sliding.schemaSlogan") as string,
      },
      serviceType: t("sliding.schemaServiceType") as string,
      description: t("sliding.schemaDesc") as string,
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: slidingDoorFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    const faqScript = document.createElement("script");
    faqScript.setAttribute("type", "application/ld+json");
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, [currentLang, t]);

  return (
    <div className="font-roboto">
      <HeaderStrip />
      <HeroSection />
      <LeadFormSection defaultService="sliding-door" />
      <AboutServiceSection
        badge={t("sliding.eyebrow") as string}
        imageSrc={aboutImageSrc}
        imageAlt={t("sliding.aboutImageAlt") as string}
        heading={t("sliding.aboutHeading") as string}
        lead={t("sliding.intro") as string}
        bullets={[
          t("sliding.aboutBullet1") as string,
          t("sliding.aboutBullet2") as string,
          t("sliding.aboutBullet3") as string,
          t("sliding.aboutBullet4") as string,
          t("sliding.aboutBullet5") as string,
        ]}
        closing={t("sliding.aboutClosing") as string}
      />
      <StatsSection />
      <CommonProblems />
      <WhyFail />
      <ProcessSection />
      <ScopeSection />
      <NeighborhoodCoverage />
      <FaqSection items={slidingDoorFaqs} heading={t("sliding.faqH2") as string} />
      <ServiceAreasGrid
        serviceName={t("home.servicesSliding") as string}
        serviceSlug="sliding-door-repair"
        heading={t("sliding.areasH2") as string}
      />
      <RelatedServicesBlockService
        heading={t("common.relatedServicesH2") as string}
        links={[
          { label: t("sliding.related1") as string, path: "/window-hardware-repair/" },
          { label: t("sliding.related2") as string, path: "/weatherstripping-sealing/" },
          { label: t("sliding.related3") as string, path: "/emergency-board-up/" },
        ]}
      />
      <BottomCta />
    </div>
  );
}